import React, { useEffect, useState, useMemo, useCallback } from 'react';
import './ShopPage.css';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import NearMeRoundedIcon from '@mui/icons-material/NearMeRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { getFullImageUrl } from './imageUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.spayis.com/api';

function formatPrice(value, currency) {
  return `${Number(value || 0).toLocaleString('fr-FR')} ${currency || 'XAF'}`;
}

function groupByCategory(products) {
  const map = new Map();
  products.forEach((p) => {
    const key = p.categoryId?.name || 'Autres';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });
  return Array.from(map.entries()).map(([title, items]) => ({ title, items }));
}

export default function ShopPage() {
  const [code] = useState(() => new URLSearchParams(window.location.search).get('code'));
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [following, setFollowing] = useState(false);

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filterDraftMin, setFilterDraftMin] = useState('');
  const [filterDraftMax, setFilterDraftMax] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchShop = useCallback(async () => {
    if (!code) {
      setLoadState('error');
      setLoadError('Aucun code de boutique fourni dans le lien.');
      return;
    }
    setLoadState('loading');
    try {
      const res = await fetch(`${API_BASE}/shops/profile/${encodeURIComponent(code)}`);
      if (!res.ok) {
        throw new Error(res.status === 404 ? "Ce code de boutique est invalide." : 'Impossible de charger la boutique.');
      }
      const data = await res.json();
      setShop(data.shop);
      setProducts(data.products?.data || []);
      setTotal(data.products?.total ?? data.products?.data?.length ?? 0);
      setLoadState('ready');
    } catch (err) {
      setLoadError(err.message || 'Une erreur est survenue.');
      setLoadState('error');
    }
  }, [code]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  const sections = useMemo(() => groupByCategory(products), [products]);
  const categories = useMemo(() => sections.map((s) => s.title), [sections]);

  const visibleSections = useMemo(() => {
    let filtered = activeCategory ? sections.filter((s) => s.title === activeCategory) : sections;
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered
        .map((s) => ({ ...s, items: s.items.filter((p) => p.name?.toLowerCase().includes(term)) }))
        .filter((s) => s.items.length > 0);
    }
    if (minPrice || maxPrice) {
      const min = minPrice ? Number(minPrice) : 0;
      const max = maxPrice ? Number(maxPrice) : Infinity;
      filtered = filtered
        .map((s) => ({ ...s, items: s.items.filter((p) => p.price >= min && p.price <= max) }))
        .filter((s) => s.items.length > 0);
    }
    return filtered;
  }, [sections, activeCategory, search, minPrice, maxPrice]);

  const priceFilterCount = (minPrice ? 1 : 0) + (maxPrice ? 1 : 0);

  const openFilter = () => {
    setFilterDraftMin(minPrice);
    setFilterDraftMax(maxPrice);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    setMinPrice(filterDraftMin);
    setMaxPrice(filterDraftMax);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraftMin('');
    setFilterDraftMax('');
  };

  const handleItinerary = () => {
    if (!shop) return;
    if (shop.latitude && shop.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${shop.latitude},${shop.longitude}`, '_blank', 'noopener');
      return;
    }
    const query = encodeURIComponent(`${shop.name || ''} ${shop.address || ''} ${shop.city || ''}`.trim());
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank', 'noopener');
  };

  const shopPhone = shop?.phoneNumber || shop?.phone;
  const canShowItinerary = !!(shop?.latitude || shop?.address || shop?.city);

  if (loadState === 'loading') {
    return (
      <div className="sp-root sp-center">
        <CircularProgress sx={{ color: '#065F46' }} />
        <p className="sp-muted" style={{ marginTop: 16 }}>Chargement de la boutique...</p>
      </div>
    );
  }

  if (loadState === 'error' || !shop) {
    return (
      <div className="sp-root sp-center">
        <ErrorOutlineRoundedIcon className="sp-error-icon" />
        <h2>Oups</h2>
        <p className="sp-muted">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="sp-root">
      <div className="sp-hero">
        {shop.mainPhoto ? (
          <img src={getFullImageUrl(shop.mainPhoto)} alt={shop.name} className="sp-hero-img" />
        ) : (
          <div className="sp-hero-placeholder">
            <StorefrontRoundedIcon style={{ fontSize: 56, color: '#b8c8d8' }} />
          </div>
        )}
        <div className="sp-shop-badge">
          {shop.logo ? (
            <img src={getFullImageUrl(shop.logo)} alt="" className="sp-shop-logo" />
          ) : (
            <StorefrontRoundedIcon style={{ color: 'white' }} />
          )}
        </div>
      </div>

      <main className="sp-main">
        <section className="sp-card sp-overview">
          <div className="sp-overview-row">
            <h1 className="sp-shop-name">{shop.name}</h1>
            {shop.isCertified && (
              <span className="sp-cert-badge">
                <VerifiedRoundedIcon fontSize="inherit" /> Certifiée
              </span>
            )}
            <button
              className={`sp-fav-btn ${following ? 'active' : ''}`}
              onClick={() => setFollowing((v) => !v)}
              aria-label="Suivre cette boutique"
            >
              {following ? <FavoriteRoundedIcon fontSize="small" /> : <FavoriteBorderRoundedIcon fontSize="small" />}
            </button>
          </div>

          <div className="sp-meta-row">
            {shop.rating !== undefined && shop.rating !== null && (
              <span className="sp-meta-chip">
                <StarRoundedIcon fontSize="inherit" style={{ color: '#F59E0B' }} />
                {Number(shop.rating).toFixed(1)}
                {shop.reviewsCount ? <span className="sp-meta-sub"> ({shop.reviewsCount})</span> : null}
              </span>
            )}
            {(shop.city || shop.region) && (
              <span className="sp-meta-chip">
                <LocationOnRoundedIcon fontSize="inherit" />
                {[shop.city, shop.region].filter(Boolean).join(', ')}
              </span>
            )}
            {shop.distance !== undefined && shop.distance !== null && (
              <span className="sp-meta-chip sp-meta-accent">
                <NearMeRoundedIcon fontSize="inherit" />
                {Number(shop.distance).toFixed(1)} km
              </span>
            )}
          </div>

          {shop.description && <p className="sp-description">{shop.description}</p>}

          {shop.address && (
            <div className="sp-location-row">
              <MapRoundedIcon fontSize="small" />
              <span>{shop.address}</span>
            </div>
          )}

          <div className="sp-actions-row">
            {canShowItinerary && (
              <button className="sp-itinerary-btn" onClick={handleItinerary}>
                <NearMeRoundedIcon fontSize="small" /> Itinéraire
              </button>
            )}
            {shopPhone && (
              <a className="sp-itinerary-btn sp-outline-btn" href={`tel:${shopPhone}`}>
                <CallRoundedIcon fontSize="small" /> Appeler
              </a>
            )}
          </div>
        </section>

        <section className="sp-search-row">
          <div className="sp-search-box">
            <SearchRoundedIcon fontSize="small" />
            <input
              className="sp-search-input"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="sp-clear-btn" onClick={() => setSearch('')} aria-label="Effacer la recherche">
                <CancelRoundedIcon fontSize="small" />
              </button>
            )}
          </div>
          <button className={`sp-filter-btn ${priceFilterCount > 0 ? 'active' : ''}`} onClick={openFilter} aria-label="Filtrer par prix">
            <TuneRoundedIcon fontSize="small" />
            {priceFilterCount > 0 && <span className="sp-filter-badge">{priceFilterCount}</span>}
          </button>
        </section>

        <div className="sp-products-header">
          <h2 className="sp-section-title">Produits</h2>
          <span className="sp-products-count">{total} article{total > 1 ? 's' : ''}</span>
        </div>

        {categories.length > 1 && (
          <div className="sp-category-chips">
            <button className={`sp-chip ${!activeCategory ? 'active' : ''}`} onClick={() => setActiveCategory(null)}>
              Tous
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`sp-chip ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {visibleSections.length === 0 ? (
          <div className="sp-empty">
            <Inventory2RoundedIcon style={{ fontSize: 40, color: '#c8d0da' }} />
            <p className="sp-muted">Aucun produit disponible.</p>
          </div>
        ) : (
          visibleSections.map((section) => (
            <section className="sp-category-section" key={section.title}>
              <div className="sp-category-header">
                <span>{section.title}</span>
                <span className="sp-category-count">
                  {section.items.length} produit{section.items.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="sp-product-grid">
                {section.items.map((product, idx) => (
                  <a
                    className="sp-product-card"
                    key={product._id || idx}
                    href={product._id ? `/produit?id=${encodeURIComponent(product._id)}` : undefined}
                  >
                    <div className="sp-product-image-wrap">
                      {product.mainImage ? (
                        <img src={getFullImageUrl(product.mainImage)} alt={product.name} className="sp-product-image" />
                      ) : (
                        <div className="sp-product-image-placeholder">
                          <ImageNotSupportedRoundedIcon fontSize="small" />
                        </div>
                      )}
                    </div>
                    <div className="sp-product-info">
                      <span className="sp-product-name">{product.name}</span>
                      <span className="sp-product-price">{formatPrice(product.price, product.currency || shop.currency)}</span>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      {filterOpen && (
        <div className="sp-sheet-overlay" onClick={() => setFilterOpen(false)}>
          <div className="sp-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sp-sheet-handle" />
            <div className="sp-sheet-header">
              <h3 className="sp-sheet-title">Filtrer les produits</h3>
              <button className="sp-sheet-close" onClick={() => setFilterOpen(false)} aria-label="Fermer">
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <p className="sp-filter-label">Prix ({shop.currency || 'XAF'})</p>
            <div className="sp-price-row">
              <div className="sp-price-field">
                <label className="sp-label" htmlFor="sp-min">Min</label>
                <input
                  id="sp-min"
                  className="sp-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={filterDraftMin}
                  onChange={(e) => setFilterDraftMin(e.target.value)}
                />
              </div>
              <span className="sp-price-sep" />
              <div className="sp-price-field">
                <label className="sp-label" htmlFor="sp-max">Max</label>
                <input
                  id="sp-max"
                  className="sp-input"
                  type="number"
                  min="0"
                  placeholder="Illimité"
                  value={filterDraftMax}
                  onChange={(e) => setFilterDraftMax(e.target.value)}
                />
              </div>
            </div>

            <div className="sp-sheet-actions">
              <button className="sp-reset-btn" onClick={resetFilter}>Réinitialiser</button>
              <button className="sp-apply-btn" onClick={applyFilter}>Appliquer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
